/**
 * Canonical socket ids, approver parsing, and escalation-path parsing for the
 * Approval step — the single source of truth shared by the socket generators,
 * the Temporal executor, authorization, the inbox/workflow-state projections,
 * the arrival classifier, and the config UI. The ids must match the keys of the
 * Approval step's input/output Zod schemas
 * (`steps/control-flow/approval.step.ts`).
 *
 * Pure data + parsing only — safe to bundle into the Temporal workflow
 * sandbox via the package barrel.
 */

/** The Approval step's subject input socket id, per configured `inputType`. */
export const APPROVAL_SUBJECT_SOCKET_ID_BY_INPUT_TYPE = {
  document: "document",
  text: "text",
  sales_order: "draftSalesOrderId",
  discount_group: "discountGroupId",
  draft_purchase_order: "draftPurchaseOrderId",
} as const;

/**
 * The outcome socket that carries the subject id when the decision is
 * "approved", per `inputType`. The document/text subjects reuse the
 * sales-order sockets (the step's original shape).
 */
export const APPROVAL_APPROVED_SOCKET_ID_BY_INPUT_TYPE = {
  document: "approvedDraftSalesOrderId",
  text: "approvedDraftSalesOrderId",
  sales_order: "approvedDraftSalesOrderId",
  discount_group: "approvedDiscountGroupId",
  draft_purchase_order: "approvedDraftPurchaseOrderId",
} as const;

/** Mirror of the approved map for the "rejected" outcome. */
export const APPROVAL_REJECTED_SOCKET_ID_BY_INPUT_TYPE = {
  document: "rejected",
  text: "rejected",
  sales_order: "rejected",
  discount_group: "rejectedDiscountGroupId",
  draft_purchase_order: "rejectedDraftPurchaseOrderId",
} as const;

/** Outcome socket taken when a timeout elapses with no decision. */
export const APPROVAL_TIMED_OUT_SOCKET_ID = "timed_out";

/** All subject input socket ids, for classifying incoming edges. */
export const APPROVAL_SUBJECT_INPUT_SOCKET_IDS: ReadonlySet<string> = new Set(
  Object.values(APPROVAL_SUBJECT_SOCKET_ID_BY_INPUT_TYPE),
);

/** All approved-outcome socket ids, for classifying outgoing edges. */
export const APPROVAL_APPROVED_OUTPUT_SOCKET_IDS: ReadonlySet<string> = new Set(
  Object.values(APPROVAL_APPROVED_SOCKET_ID_BY_INPUT_TYPE),
);

/** All rejected-outcome socket ids, for classifying outgoing edges. */
export const APPROVAL_REJECTED_OUTPUT_SOCKET_IDS: ReadonlySet<string> = new Set(
  Object.values(APPROVAL_REJECTED_SOCKET_ID_BY_INPUT_TYPE),
);

/** A validated escalation path from an Approval step's config. */
export interface ApprovalEscalationPath {
  /** Socket id of the path — a non-empty string. */
  name: string;
  /** Display label shown on the canvas socket. */
  label?: string;
  /** Optional auto-route condition groups (consumers narrow the type). */
  appliesWhen?: unknown;
}

/**
 * Parses `config.escalationPaths` into routable paths, dropping entries whose
 * `name` is missing or empty — such paths never produce an output socket, so
 * no consumer can route to them.
 */
export function parseApprovalEscalationPaths(
  config: Record<string, unknown>,
): ApprovalEscalationPath[] {
  if (!Array.isArray(config.escalationPaths)) return [];
  const paths: ApprovalEscalationPath[] = [];
  for (const raw of config.escalationPaths as Array<{
    name?: unknown;
    label?: unknown;
    appliesWhen?: unknown;
  }>) {
    if (typeof raw?.name !== "string" || raw.name.length === 0) continue;
    paths.push({
      name: raw.name,
      label: typeof raw.label === "string" ? raw.label : undefined,
      appliesWhen: raw.appliesWhen,
    });
  }
  return paths;
}

/** The escalation path socket names of an Approval step's config. */
export function approvalEscalationPathNames(
  config: Record<string, unknown>,
): Set<string> {
  return new Set(parseApprovalEscalationPaths(config).map((p) => p.name));
}

/** Per-approver notification mode on an Approval step (hierarchical): `email` →
 * email + in-app; `in_app` → in-app only; `none` → silent (no push). */
export type ApproverNotifyMode = "email" | "in_app" | "none";

/** Whether an approver entry names an organizational team or a single user. */
export type ApproverKind = "team" | "user";

/**
 * A resolved approver on a gate: which principal, and how it is notified.
 * `id` is a `um_user_groups` id when `type` is `"team"`, else a user id.
 */
export interface ApproverAssignment {
  type: ApproverKind;
  id: string;
  notify: ApproverNotifyMode;
}

function coerceNotifyMode(value: unknown): ApproverNotifyMode {
  return value === "email" || value === "none" ? value : "in_app";
}

/**
 * The effective approvers for an Approval step, as ONE polymorphic list —
 * teams and users side by side, OR-joined (any entry may decide; still exactly
 * one decision per gate). Each entry carries its own notification mode.
 *
 * `approverList` is the forward shape. The two shapes that reached production
 * are still read so persisted configs — and in-flight Temporal runs, which
 * replay the step config recorded by the `loadWorkflowDefinition` activity —
 * keep their exact behavior. Precedence, first match wins:
 *
 * 1. `approverList` — a non-empty array is authoritative.
 * 2. `{ approverType:"team", approverTeamId }` — the single-team pair; notify
 *    derived from `notifyApprovers` (true → "email" = email + in-app; false →
 *    "in_app") so delivery is unchanged.
 * 3. `approverUserId` — a single-user gate (`approverType` defaults to
 *    `"user"`, so an absent type still lands here); notify derived from
 *    `notifyApprovers` the same way, matching the legacy path where the user
 *    always got the in-app bell and email only when `notifyApprovers` was set.
 *
 * Returns `[]` when nothing is configured — a gate may still draw approvers
 * from the wired `approverEmails` input socket, which carries bare addresses
 * rather than principal ids and so is never part of this list.
 *
 * Entries are deduped on `type:id`; invalid rows are dropped. Pure — safe
 * inside the Temporal workflow sandbox, and shared by the executor,
 * authorization, inbox projections and the config UI so the forward ⇄ legacy
 * reconciliation lives in exactly one place.
 */
export function resolveApproverList(
  config: Record<string, unknown>,
): ApproverAssignment[] {
  const raw = config.approverList;
  if (Array.isArray(raw) && raw.length > 0) {
    const approvers: ApproverAssignment[] = [];
    const seen = new Set<string>();
    for (const entry of raw as Array<{
      type?: unknown;
      id?: unknown;
      notify?: unknown;
    }>) {
      const id = entry?.id;
      if (typeof id !== "string" || id.length === 0) continue;
      // Only the two known kinds route anywhere; drop anything else rather than
      // guessing a default, so a malformed row can never silently become a team.
      const type = entry?.type;
      if (type !== "team" && type !== "user") continue;
      const key = `${type}:${id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      approvers.push({ type, id, notify: coerceNotifyMode(entry?.notify) });
    }
    return approvers;
  }

  // A stale `approverTeamId` on a user gate is NOT authoritative — `approverType`
  // decides, mirroring the shape's original semantics.
  if (config.approverType === "team") {
    if (
      typeof config.approverTeamId === "string" &&
      config.approverTeamId.length > 0
    ) {
      return [
        {
          type: "team",
          id: config.approverTeamId,
          notify: config.notifyApprovers === true ? "email" : "in_app",
        },
      ];
    }

    return [];
  }

  if (
    typeof config.approverUserId === "string" &&
    config.approverUserId.length > 0
  ) {
    return [
      {
        type: "user",
        id: config.approverUserId,
        notify: config.notifyApprovers === true ? "email" : "in_app",
      },
    ];
  }

  return [];
}
